/**
 * @fileoverview FacilitySolve — AI Triage System frontend service
 *
 * API client for creating and managing AI-powered triage sessions.
 *
 * @author FacilityAI
 * @version 1.0.0
 */

import { CORE_URL } from "@/constants/api";
import { VITE_CORE_API_URL } from "@/constants/env";
import { auth } from "@/services/auth-service";

const BASE = `${VITE_CORE_API_URL}${CORE_URL}/organization/facility-solve`;

async function getToken(): Promise<string> {
	const token = await auth?.currentUser?.getIdToken();
	if (!token) throw new Error("Authentication required");
	return token;
}

async function parseResponse<T>(res: Response): Promise<T> {
	const text = await res.text();
	let json: any;
	try {
		json = JSON.parse(text);
	} catch {
		throw new Error(`Server returned invalid response (HTTP ${res.status})`);
	}
	if (!json.success) throw new Error(json.message ?? "Request failed");
	return json.data;
}

async function apiGet<T>(path: string): Promise<T> {
	const token = await getToken();
	const res = await fetch(`${BASE}${path}`, {
		method: "GET",
		headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
	});
	return parseResponse<T>(res);
}

async function apiPost<T>(path: string, body: unknown): Promise<T> {
	const token = await getToken();
	const res = await fetch(`${BASE}${path}`, {
		method: "POST",
		headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
		body: JSON.stringify(body),
	});
	return parseResponse<T>(res);
}

async function apiPut<T>(path: string, body: unknown): Promise<T> {
	const token = await getToken();
	const res = await fetch(`${BASE}${path}`, {
		method: "PUT",
		headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
		body: JSON.stringify(body),
	});
	return parseResponse<T>(res);
}

// ─── Types ──────────────────────────────────────────────────────────────────

export type SessionStatus = "ACTIVE" | "RESOLVED_SELF" | "ESCALATED" | "ABANDONED";
export type SessionUrgency = "LOW" | "MEDIUM" | "HIGH" | "CRITICAL";
export type SessionCategory = "HVAC" | "Plumbing" | "Electrical" | "Roofing" | "Fire Protection" | "Refrigeration" | "General Maintenance" | "Janitorial" | "Elevator" | "Other";
export type StepType = "AI_QUESTION" | "USER_RESPONSE" | "AI_DIAGNOSIS" | "AI_INSTRUCTION" | "RESOLUTION" | "ESCALATION";

export interface StepContent {
	text?: string;
	options?: { id: string; label: string }[];
	selectedOption?: string;
	userText?: string;
	instructions?: string[];
	diagnosis?: {
		rootCause: string;
		confidence: number;
		suggestedActions: string[];
	};
}

export interface TriageStep {
	id: number;
	sessionId: number;
	stepNumber: number;
	stepType: StepType;
	content: StepContent;
	createdAt: string;
}

export interface TriageSession {
	id: number;
	organizationId: number;
	userId: number;
	locationId: number | null;
	assetId: number | null;
	scanId: number | null;
	title: string;
	description: string | null;
	category: SessionCategory | null;
	urgency: SessionUrgency;
	status: SessionStatus;
	aiDiagnosis: { rootCause: string; confidence: number; suggestedActions: string[] } | null;
	resolution: { outcome: string; summary: string; stepsTaken: string[] } | null;
	workOrderId: number | null;
	createdAt: string;
	updatedAt: string;
}

export interface TriageSessionWithSteps extends TriageSession {
	steps: TriageStep[];
}

export interface TriageSessionListItem extends TriageSession {
	userName?: string;
	locationName?: string;
	assetName?: string;
}

export interface CreateSessionInput {
	title: string;
	description?: string;
	category?: SessionCategory;
	urgency?: SessionUrgency;
	locationId?: number;
	assetId?: number;
	scanId?: number;
}

// ─── API Functions ──────────────────────────────────────────────────────────

export async function createTriageSession(input: CreateSessionInput): Promise<TriageSessionWithSteps> {
	return apiPost<TriageSessionWithSteps>("", input);
}

export async function getTriageSessions(filters?: { status?: string; locationId?: number }): Promise<TriageSessionListItem[]> {
	const params = new URLSearchParams();
	if (filters?.status) params.set("status", filters.status);
	if (filters?.locationId) params.set("locationId", String(filters.locationId));
	const qs = params.toString();
	const data = await apiGet<{ sessions: TriageSessionListItem[] }>(qs ? `?${qs}` : "");
	return data.sessions;
}

export async function getTriageSession(id: number): Promise<TriageSessionWithSteps> {
	return apiGet<TriageSessionWithSteps>(`/${id}`);
}

export async function getTriageSessionsByWorkOrder(workOrderId: number): Promise<TriageSessionListItem[]> {
	const data = await apiGet<{ sessions: TriageSessionListItem[] }>(`/by-work-order/${workOrderId}`);
	return data.sessions;
}

export async function submitTriageStep(sessionId: number, input: { userText?: string; selectedOption?: string }): Promise<{ userStep: TriageStep; aiStep: TriageStep }> {
	return apiPost<{ userStep: TriageStep; aiStep: TriageStep }>(`/${sessionId}/step`, input);
}

export async function resolveTriageSession(sessionId: number, summary: string): Promise<TriageSession> {
	return apiPut<TriageSession>(`/${sessionId}/resolve`, { summary });
}

export async function escalateTriageSession(sessionId: number): Promise<{ session: TriageSession; workOrderId: number }> {
	return apiPost<{ session: TriageSession; workOrderId: number }>(`/${sessionId}/escalate`, {});
}
